/*
 *
 * Copyright (C) 2022 NetBase Solutions, Inc.
 * 3960 Freedom Circle Suite 201 Santa Clara, CA 95054
 * All rights reserved.
 */

/* eslint-disable import/prefer-default-export */

import messageUtil from 'utils/messageUtil'
import { AnnotatedMetricResult } from 'schemas/AnnotatedMetricResultSchema'
import { DashboardWidgetData } from 'features/widget/types'
import { SocialWebSourcesWidget } from 'schemas/SocialWebSourcesWidgetSchema'
import CountType, { CountTypeStrings } from '../../../../enums/CountType'
import { fromSearchResult } from '../../../../utils/timelineDataUtil'
import MetricSeries from '../../../../enums/MetricSeries'
import Source from '../../../../enums/Source'
import { hasInternalAnalysisPermission } from '../../../../utils/permissionUtil'
import { addTz } from '../../../../utils/timeZoneUtil'

const getMetricSeriesByCountType = (countType: CountTypeStrings) => {
  switch (countType) {
    case CountType.MENTIONS: {
      return [
        MetricSeries.SOURCE_TWITTER,
        MetricSeries.SOURCE_FACEBOOK,
        MetricSeries.SOURCE_INSTAGRAM,
        MetricSeries.SOURCE_YOU_TUBE,
        MetricSeries.SOURCE_TIKTOK,
        MetricSeries.SOURCE_BLUESKY,
        MetricSeries.SOURCE_REDDIT,
        MetricSeries.SOURCE_BLOGS,
        MetricSeries.SOURCE_COMMENTS,
        MetricSeries.SOURCE_FORUMS,
        MetricSeries.SOURCE_INTERNAL,
        MetricSeries.SOURCE_MICROBLOGS,
        MetricSeries.SOURCE_NEWS,
        MetricSeries.SOURCE_BROADCAST,
        MetricSeries.SOURCE_CONSUMER_REVIEWS,
        MetricSeries.SOURCE_PROF_REVIEWS,
        MetricSeries.SOURCE_SOCIAL_NETWORKS,
        MetricSeries.SOURCE_OTHER
      ]
    }
    case CountType.POSTS: {
      return [
        MetricSeries.SOURCE_TWITTER_POSTS,
        MetricSeries.SOURCE_FACEBOOK_POSTS,
        MetricSeries.SOURCE_INSTAGRAM_POSTS,
        MetricSeries.SOURCE_YOU_TUBE_POSTS,
        MetricSeries.SOURCE_TIKTOK_POSTS,
        MetricSeries.SOURCE_BLUESKY_POSTS,
        MetricSeries.SOURCE_REDDIT_POSTS,
        MetricSeries.SOURCE_BLOGS_POSTS,
        MetricSeries.SOURCE_COMMENTS_POSTS,
        MetricSeries.SOURCE_FORUMS_POSTS,
        MetricSeries.SOURCE_INTERNAL_POSTS,
        MetricSeries.SOURCE_MICROBLOGS_POSTS,
        MetricSeries.SOURCE_NEWS_POSTS,
        MetricSeries.SOURCE_BROADCAST_POSTS,
        MetricSeries.SOURCE_CONSUMER_REVIEWS_POSTS,
        MetricSeries.SOURCE_PROF_REVIEWS_POSTS,
        MetricSeries.SOURCE_SOCIAL_NETWORKS_POSTS,
        MetricSeries.SOURCE_OTHER_POSTS
      ]
    }
    default:
      throw new Error(`Unknown Count Type: ${countType}`)
  }
}

const sourceToMetricSeriesMap = {
  [Source.BLOGS]: MetricSeries.SOURCE_BLOGS,
  [Source.COMMENTS]: MetricSeries.SOURCE_COMMENTS,
  [Source.CONSUMER_REVIEWS]: MetricSeries.SOURCE_CONSUMER_REVIEWS,
  [Source.FORUMS]: MetricSeries.SOURCE_FORUMS,
  [Source.MICROBLOGS]: MetricSeries.SOURCE_MICROBLOGS,
  [Source.NEWS]: MetricSeries.SOURCE_NEWS,
  [Source.BROADCAST]: MetricSeries.SOURCE_BROADCAST,
  [Source.SOCIAL_NETWORKS]: MetricSeries.SOURCE_SOCIAL_NETWORKS,
  [Source.PROF_REVIEWS]: MetricSeries.SOURCE_PROF_REVIEWS,
  [Source.OTHER]: MetricSeries.SOURCE_OTHER,
  [Source.TWITTER]: MetricSeries.SOURCE_TWITTER,
  [Source.INTERNAL]: MetricSeries.SOURCE_INTERNAL,
  [Source.FACEBOOK]: MetricSeries.SOURCE_FACEBOOK,
  [Source.INSTAGRAM]: MetricSeries.SOURCE_INSTAGRAM,
  [Source.YOU_TUBE]: MetricSeries.SOURCE_YOU_TUBE,
  [Source.TIKTOK]: MetricSeries.SOURCE_TIKTOK,
  [Source.BLUESKY]: MetricSeries.SOURCE_BLUESKY,
  [Source.REDDIT]: MetricSeries.SOURCE_REDDIT
}

const sourceToMetricSeriesPostMap = {
  [Source.BLOGS]: MetricSeries.SOURCE_BLOGS_POSTS,
  [Source.COMMENTS]: MetricSeries.SOURCE_COMMENTS_POSTS,
  [Source.CONSUMER_REVIEWS]: MetricSeries.SOURCE_CONSUMER_REVIEWS_POSTS,
  [Source.FORUMS]: MetricSeries.SOURCE_FORUMS_POSTS,
  [Source.MICROBLOGS]: MetricSeries.SOURCE_MICROBLOGS_POSTS,
  [Source.NEWS]: MetricSeries.SOURCE_NEWS_POSTS,
  [Source.BROADCAST]: MetricSeries.SOURCE_BROADCAST_POSTS,
  [Source.SOCIAL_NETWORKS]: MetricSeries.SOURCE_SOCIAL_NETWORKS_POSTS,
  [Source.PROF_REVIEWS]: MetricSeries.SOURCE_PROF_REVIEWS_POSTS,
  [Source.OTHER]: MetricSeries.SOURCE_OTHER_POSTS,
  [Source.TWITTER]: MetricSeries.SOURCE_TWITTER_POSTS,
  [Source.INTERNAL]: MetricSeries.SOURCE_INTERNAL_POSTS,
  [Source.FACEBOOK]: MetricSeries.SOURCE_FACEBOOK_POSTS,
  [Source.INSTAGRAM]: MetricSeries.SOURCE_INSTAGRAM_POSTS,
  [Source.YOU_TUBE]: MetricSeries.SOURCE_YOU_TUBE_POSTS,
  [Source.TIKTOK]: MetricSeries.SOURCE_TIKTOK_POSTS,
  [Source.BLUESKY]: MetricSeries.SOURCE_BLUESKY_POSTS,
  [Source.REDDIT]: MetricSeries.SOURCE_REDDIT_POSTS
}

const sourceFiltersToMetricSeriesList = (widgetSetting: SocialWebSourcesWidget) => {
  const { sources, countType } = widgetSetting
  const convertMap =
    countType === CountType.POSTS ? sourceToMetricSeriesPostMap : sourceToMetricSeriesMap

  return sources!.map((source) => convertMap[source!]).filter((metricSeries) => metricSeries) // youtube is in filter,but not in source metric series
}

const applySourceFilter = (widgetSetting: SocialWebSourcesWidget, metricSeriesList: string[]) => {
  if (!widgetSetting.sources || !widgetSetting.sources.length) {
    return metricSeriesList
  }

  const metricSeriesFromFilter = sourceFiltersToMetricSeriesList(widgetSetting)
  return metricSeriesList.filter((metricSeries) => metricSeriesFromFilter.includes(metricSeries))
}

const provisionInternalSource = (metricSeriesList: string[], pageConfigUser: any) =>
  metricSeriesList.filter(
    (metricSeries) =>
      hasInternalAnalysisPermission(pageConfigUser.userPermissions) ||
      ![MetricSeries.SOURCE_INTERNAL_POSTS, MetricSeries.SOURCE_INTERNAL].includes(metricSeries)
  )

export const getSearchMetricSeriesList = (
  widgetSetting: SocialWebSourcesWidget,
  pageConfigUser: any
) => {
  const metricSeriesFromCountType = getMetricSeriesByCountType(widgetSetting.countType!)
  const provisionedMetricSeriesList = provisionInternalSource(
    metricSeriesFromCountType,
    pageConfigUser
  )
  return applySourceFilter(widgetSetting, provisionedMetricSeriesList)
}

export const convertToAreaData = (
  data: DashboardWidgetData<AnnotatedMetricResult>[],
  widgetSetting: SocialWebSourcesWidget,
  pageConfigUser: any
) => {
  const timelineData = fromSearchResult(
    data,
    [widgetSetting.selectedTopic],
    widgetSetting.timeUnit!,
    getSearchMetricSeriesList(widgetSetting, pageConfigUser),
    widgetSetting.viewOption!,
    pageConfigUser.timeZone
  )

  const { series, columns } = timelineData

  const areaData = series!.map((entry) => ({
    name: messageUtil.sources(entry.type!),
    data: entry!.data!.map((item, index) => ({
      x: addTz(columns![index]!, pageConfigUser.timeZone),
      y: item
    }))
  }))

  return areaData
}
